'use client';

import * as React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Droplets,
  Lightbulb,
  Recycle,
  ShieldCheck,
  UsersRound,
  Workflow,
} from 'lucide-react';
import CmsImage from '@/components/ui/CmsImage';
import { stripHtml, getImageUrl } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { SustainabilityCommitments } from '@/types/sustainability';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/components/ui/utils';
import { useCarouselNeedsNavigation } from '@/hooks/useCarouselNeedsNavigation';

type CommitmentTab = '2030' | '2040';

type CommitmentCard = {
  id: string;
  title: string;
  description: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

type StandardItem = {
  id: string;
  title: string;
  description: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const TAB_LABELS: Record<CommitmentTab, string> = {
  '2030': '2030',
  '2040': '2040 & Beyond',
};

const COMMITMENT_DATA: Record<CommitmentTab, CommitmentCard[]> = {
  '2030': [
    {
      id: 'gender-diversity',
      title: 'Gender Diversity',
      description: "Increase women's representation in the workforce to at least 5%.",
      Icon: UsersRound,
    },
    {
      id: 'board-governance',
      title: 'Board Governance',
      description: 'Appoint at least 20% female directors to the Board of Wonder Cement.',
      Icon: Workflow,
    },
    {
      id: 'empowerment',
      title: 'Empowerment',
      description: 'Double annual learning and development hours provided to every employee.',
      Icon: Lightbulb,
    },
  ],
  '2040': [
    {
      id: 'renewables',
      title: 'Renewable Expansion',
      description: 'Maximise renewable energy share with next-gen WHRS and solar installations.',
      Icon: Lightbulb,
    },
    {
      id: 'water-positive',
      title: 'Water Positive',
      description:
        'Advance water-positive operations through circular water and recharge projects.',
      Icon: Droplets,
    },
    {
      id: 'green-products',
      title: 'Green Portfolio',
      description: 'Expand low-carbon product portfolio with enhanced material efficiency.',
      Icon: Recycle,
    },
  ],
};

function getCmsTabItems(src: SustainabilityCommitments | undefined, year: CommitmentTab) {
  const yearWrapper = src?.sectionLeft?.find((y) =>
    y.heading?.toLowerCase().startsWith(year.toLowerCase()),
  );
  if (!yearWrapper || !yearWrapper.tabItem || yearWrapper.tabItem.length === 0) {
    return null;
  }
  return yearWrapper.tabItem.map((it) => ({
    id: String(it.id),
    title: it.heading,
    description: stripHtml(it.description),
    iconSrc: getImageUrl(it.media as never) || null,
    // CommitmentsTabCarousel expects an Icon; use Lightbulb as default
    Icon: Lightbulb as React.ComponentType<React.SVGProps<SVGSVGElement>>,
  }));
}

const STANDARDS: StandardItem[] = [
  {
    id: 'water',
    title: 'Water Stewardship',
    description: 'Continuously advance water positivity, scaling toward long-term sustainability.',
    Icon: Droplets,
  },
  {
    id: 'waste',
    title: 'Zero Waste',
    description: 'Maintain zero waste to landfill by ensuring material reuse and co-processing.',
    Icon: Recycle,
  },
  {
    id: 'safety',
    title: 'Safety Mandate',
    description:
      'Uphold a Zero Harm policy with zero fatalities and minimal LTIs across operations.',
    Icon: ShieldCheck,
  },
];

function CommitmentCardArticle({ item }: { item: CommitmentCard }) {
  return (
    <article className="h-full min-h-[168px] rounded-[12px] border border-white/10 bg-gradient-to-b from-[#2a2b2e] to-[#222326] px-4 py-4 sm:min-h-[184px]">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/78 sm:h-10 sm:w-10">
        <item.Icon className="h-4 w-4" />
      </div>
      <h3 className="mt-6 text-[15px] font-medium text-white sm:mt-8">{item.title}</h3>
      <p className="mt-2 text-[13px] leading-[1.55] text-white/44">{item.description}</p>
    </article>
  );
}

function CommitmentsTabCarousel({ items }: { items: CommitmentCard[] }) {
  const [api, setApi] = React.useState<CarouselApi>();
  const needsNavigation = useCarouselNeedsNavigation(api, items.length);
  const isPair = items.length === 2;

  if (isPair) {
    return (
      <div className="mt-4 sm:mt-5">
        <div className="mx-auto flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
          {items.map((item) => (
            <div
              key={item.id}
              className="w-full sm:max-w-[calc(50%-0.375rem)] lg:max-w-[calc(33.333%-0.75rem)]"
            >
              <CommitmentCardArticle item={item} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 sm:mt-5">
      <Carousel setApi={setApi} opts={{ align: 'start', loop: false }} className="w-full">
        <CarouselContent className="-ml-3">
          {items.map((item) => (
            <CarouselItem
              key={item.id}
              className="basis-[86%] pl-3 sm:basis-[48%] md:basis-[32%] lg:basis-1/3"
            >
              <CommitmentCardArticle item={item} />
            </CarouselItem>
          ))}
        </CarouselContent>

        {needsNavigation ? (
          <div className="mt-4 flex items-center justify-center gap-2 md:mt-5">
            <button
              type="button"
              onClick={() => api?.scrollPrev()}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-black/10 bg-white text-[#0f0f10] transition-colors hover:bg-white/90 sm:h-8 sm:w-8"
              aria-label="Previous commitments"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => api?.scrollNext()}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-black/10 bg-white text-[#0f0f10] transition-colors hover:bg-white/90 sm:h-8 sm:w-8"
              aria-label="Next commitments"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </Carousel>
    </div>
  );
}

interface SustainabilityCommitmentsSectionProps {
  commitments?: SustainabilityCommitments;
}

export default function SustainabilityCommitmentsSection({
  commitments,
}: SustainabilityCommitmentsSectionProps) {
  const tabs: CommitmentTab[] = ['2030', '2040'];

  const cmsHeading = commitments?.heading?.trim();
  const cmsSubheading = stripHtml(commitments?.subHeading);

  const cmsLeftByYear: Record<CommitmentTab, ReturnType<typeof getCmsTabItems>> = {
    '2030': getCmsTabItems(commitments, '2030'),
    '2040': getCmsTabItems(commitments, '2040'),
  };

  const cmsRight =
    commitments?.sectionRight?.listItem && commitments.sectionRight.listItem.length > 0
      ? {
          heading: commitments.sectionRight.heading,
          items: commitments.sectionRight.listItem.map((it) => ({
            id: String(it.id),
            heading: it.heading,
            description: stripHtml(it.description),
            iconSrc: getImageUrl(it.media as never) || null,
          })),
        }
      : null;

  return (
    <section className="relative z-10 bg-black text-white">
      <div className="container mx-auto max-sm:px-4">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-[1.6rem] font-bold leading-tight tracking-tight sm:text-4xl md:text-[2.55rem] lg:text-[3rem]">
            {cmsHeading || 'Our Long-Term Commitments'}
          </h2>
          <p className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-white/65 sm:mt-4 sm:text-base md:text-[17px]">
            {cmsSubheading ||
              'We have established clear, time-bound targets to measure our progress and hold our leadership accountable to our 2030, 2035, 2040, and 2050 visions.'}
          </p>
        </div>

        <div className="mt-7 grid grid-cols-1 gap-4 sm:mt-9 md:gap-5 lg:grid-cols-[1.4fr_1fr]">
          <div className="rounded-xl border border-white/10 bg-[#171719] p-4 sm:p-5 lg:min-h-[355px] ">
            <Tabs defaultValue="2030" className="w-full">
              <TabsList className="grid h-11 w-full grid-cols-2 rounded-full border border-white/5 bg-transparent p-1 sm:h-12">
                {tabs.map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className={cn(
                      'whitespace-nowrap rounded-full border border-transparent px-2 text-[13px] font-medium text-white/35 transition-all sm:text-sm md:text-[1rem]',
                      'data-[state=active]:border-white/10 data-[state=active]:bg-white data-[state=active]:text-[#151516]',
                    )}
                  >
                    {TAB_LABELS[tab]}
                  </TabsTrigger>
                ))}
              </TabsList>

              {tabs.map((tab) => (
                <TabsContent key={tab} value={tab} className="mt-4 outline-none">
                  <div className="mb-1 border-t border-white/10" />
                  <CommitmentsTabCarousel items={cmsLeftByYear[tab] ?? COMMITMENT_DATA[tab]} />
                </TabsContent>
              ))}
            </Tabs>
          </div>

          <aside className="relative min-h-[420px] overflow-hidden rounded-xl border border-white/10 sm:min-h-[440px] md:min-h-[460px] lg:min-h-[385px]">
            <CmsImage
              src="/images/stand_iom.jpg"
              alt="Ongoing performance standards"
              fill
              className="object-cover"
              sizes="(min-width: 1024px) 36vw, 100vw"
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute inset-4 overflow-y-auto rounded-2xl bg-white/50 px-4 py-4 text-[#151515] backdrop-blur-sm sm:inset-5 sm:px-5 sm:py-5 lg:inset-6 lg:py-6">
              <h3 className="text-lg font-semibold leading-tight tracking-tight sm:text-xl lg:text-xl">
                {cmsRight?.heading || 'Ongoing Performance Standards'}
              </h3>
              <ul className="mt-5 space-y-4 sm:mt-6 sm:space-y-5 lg:mt-8">
                {(cmsRight
                  ? cmsRight.items.map((item) => ({
                      id: item.id,
                      title: item.heading,
                      description: item.description,
                      Icon: Lightbulb,
                    }))
                  : STANDARDS
                ).map(({ id, title, description, Icon }) => (
                  <li key={id} className="flex gap-3 sm:gap-4">
                    <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center text-black/85 sm:h-12 sm:w-12 lg:h-14 lg:w-14">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[15px] font-semibold leading-tight sm:text-base lg:text-[1.08rem]">
                        {title}
                      </p>
                      <p className="mt-1 text-[11px] leading-[1.5] text-black/58">{description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
